import { useOverlayState } from "features/content-overlay"
import DecisionModal from "features/modals/DecisionModal"
import { get, has, pick } from "nodash"
import { useCallback } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { disabledBackgroundCloseAction } from "../slice"
import { DecisionContext, initializedAction } from "./decisionSlice"
import { selectLastDecision } from "./decisionSelectors"

type DecisionModalConfiguration = {
    backgroundNoop?: boolean
    fromWithinExistingModal?: boolean
}

export const useDecisionModal = (
    defaultContext: DecisionContext,
    configuration: DecisionModalConfiguration = {}
) => {
    const dispatch = useAppDispatch()
    const { showContent, hideOverlay } = useOverlayState()
    const decision = useAppSelector(selectLastDecision)

    const show = useCallback(
        (contextOverride?: DecisionContext) => {
            const context = contextOverride || defaultContext
            if (!context || !has(context, "options")) {
                throw "useDecisionModal requires a valid context parameter"
            }

            dispatch(
                initializedAction(
                    // better safe then sorry, in case the dev fell asleep
                    pick(context, ["title", "description", "options", "id"])
                )
            )

            // @todo -- make this cleaner, as it currently relies on the overlay defaulting back to closeOnClick = true...when modals are hidden
            if (!get(configuration, "backgroundNoop")) {
                dispatch(disabledBackgroundCloseAction())
            }

            // if called from existing modal, running this would mess with a few things, so we bypass it. Instead, the modals container component must deal with any decisions being initialized
            if (!get(configuration, "fromWithinExistingModal")) {
                showContent(DecisionModal)
            }
        },
        [defaultContext, showContent, configuration, dispatch]
    )

    return {
        decision,
        decisionWasMade: decision,
        // note that passing show directly to onclick will put the event object into the state! so do not do that, instead use showSafely
        // if you need to pass custom context, then use show with the contextOverride parameter
        show,
        showSafely: () => show(),
        hide: hideOverlay,
    }
}

export default useDecisionModal
